import {
  ChevronRight,
  ChevronLeft,
  Loader2,
  Edit3,
  AlertCircle,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { useTranslation } from "react-i18next";
import {
  StepProps,
  validateInitialDraft,
  validateDescription,
  DESCRIPTION_MIN_CHARS,
  DESCRIPTION_MAX_CHARS,
} from "../types";

interface StepOneProps extends StepProps {
  jobDescription: string;
  descriptionOfRoleRaw: string;
  onDescriptionChange: (description: string) => void;
  onRawTextChange: (rawText: string) => void;
  onBack?: () => void;
  onForward?: () => void;
  canGoBack?: boolean;
  canGoForward?: boolean;
  hasAiExpanded?: boolean;
  isExpandingWithAi?: boolean;
  onExpandWithAi?: () => void;
  /** Vault documents the introduction was grounded in; empty when none were. */
  aiSourceDocuments?: string[];
}

export const StepOne = ({
  onNext,
  isProcessing,
  jobDescription,

  onDescriptionChange,
  onRawTextChange,
  onBack,
  canGoBack = false,
  hasAiExpanded = false,
  isExpandingWithAi = false,
  onExpandWithAi,
  aiSourceDocuments = [],
}: StepOneProps) => {
  const { t } = useTranslation("recruit");

  // If AI hasn't expanded yet, validate as initial draft
  // If AI has expanded, validate with character limits
  const validation = hasAiExpanded
    ? validateDescription(jobDescription)
    : validateInitialDraft(jobDescription);

  const canProceed = validation.isValid && !isProcessing && !isExpandingWithAi;
  const canExpandWithAi = validation.isValid && !isExpandingWithAi;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center text-white text-sm font-medium">
          1
        </div>
        <h2 className="text-xl font-semibold mb-0">
          {hasAiExpanded
            ? t("ads.createModal.step1.titleReview")
            : t("ads.createModal.step1.titleDraft")}
        </h2>
      </div>

      {!hasAiExpanded ? (
        // Initial draft phase - simple textarea, no character limits
        <>
          <p className="text-muted-foreground">
            {t("ads.createModal.step1.descriptionDraft")}
          </p>
          <Textarea
            placeholder={t("ads.createModal.step1.placeholder")}
            className="min-h-[120px]"
            value={jobDescription}
            onChange={(e) => onDescriptionChange(e.target.value)}
          />
          {!validation.isValid && (
            <p className="text-sm text-red-500 mt-2">{validation.error}</p>
          )}
        </>
      ) : (
        // Advanced editing phase - rich text editor with character limits
        <>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                  Granska AI-genererat innehåll
                </h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  AI:n har skapat en annonsintroduktion i pitch-stil baserat på
                  er beskrivning.
                  <strong> Granska innehållet</strong> och justera ton, fakta
                  och call-to-action så att det passar er och lockar rätt
                  kandidater.
                </p>
                {/* What the company facts in the text rest on. Named documents
                    are checkable; the absence of them is the signal to read the
                    company paragraph with more suspicion. */}
                {aiSourceDocuments.length > 0 ? (
                  <p
                    className="text-sm text-blue-700 dark:text-blue-300 mt-2 flex items-start gap-1.5"
                    data-testid="ad-ai-grounding"
                  >
                    <FileText className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                      {t("ads.createModal.step1.groundedIn", {
                        documents: aiSourceDocuments.join(", "),
                      })}
                    </span>
                  </p>
                ) : (
                  <p
                    className="text-sm text-blue-700/80 dark:text-blue-300/80 mt-2"
                    data-testid="ad-ai-grounding"
                  >
                    {t("ads.createModal.step1.notGrounded")}
                  </p>
                )}
              </div>
            </div>
          </div>

          <p className="text-muted-foreground">
            Använd formateringsverktygen för att strukturera texten. Introduktionen
            måste vara mellan {DESCRIPTION_MIN_CHARS}–{DESCRIPTION_MAX_CHARS}{" "}
            tecken.
          </p>

          <div className="space-y-2">
            <RichTextEditor
              content={jobDescription}
              onChange={onDescriptionChange}
              onRawTextChange={onRawTextChange}
              placeholder="AI:n har skapat en introduktion. Granska och redigera innehållet..."
              minCharacters={DESCRIPTION_MIN_CHARS}
              maxCharacters={DESCRIPTION_MAX_CHARS}
              className="w-full"
            />

            {!validation.isValid && (
              <p className="text-sm text-red-500 mt-2">{validation.error}</p>
            )}
          </div>
        </>
      )}

      <div className="flex justify-between items-center pt-4 border-t">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={!canGoBack}
          className="flex items-center gap-2"
        >
          <ChevronLeft className="h-4 w-4" />
          Föregående
        </Button>

        <div className="flex gap-2">
          {!hasAiExpanded ? (
            // Initial phase: Only AI expansion button
            <Button
              onClick={onExpandWithAi}
              disabled={!canExpandWithAi}
              className="flex items-center gap-2"
            >
              {isExpandingWithAi ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Edit3 className="h-4 w-4" />
              )}
              {isExpandingWithAi
                ? "AI skapar introduktion..."
                : "Skapa annonsintroduktion"}
            </Button>
          ) : (
            // After AI expansion: Proceed to next step
            <Button
              onClick={onNext}
              disabled={!canProceed}
              className="flex items-center gap-2"
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Fortsätt
                  <ChevronRight className="h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepOne;
